import { __decorate, __param } from 'vscode/external/tslib/tslib.es6.js';
import { IInstantiationService } from 'vscode/vscode/vs/platform/instantiation/common/instantiation';
import { Disposable } from 'vscode/vscode/vs/base/common/lifecycle';
import { TerminalExtensions } from 'vscode/vscode/vs/platform/terminal/common/terminal';
import { TerminalInstance } from './terminalInstance.js';
import { IContextKeyService } from 'vscode/vscode/vs/platform/contextkey/common/contextkey.service';
import { Emitter } from 'vscode/vscode/vs/base/common/event';
import { TerminalContextKeys } from 'vscode/vscode/vs/workbench/contrib/terminal/common/terminalContextKey';
import { Registry } from 'vscode/vscode/vs/platform/registry/common/platform';
import { IWorkbenchEnvironmentService } from 'vscode/vscode/vs/workbench/services/environment/common/environmentService.service';
import { promiseWithResolvers } from 'vscode/vscode/vs/base/common/async';

let TerminalInstanceService = class TerminalInstanceService extends Disposable {
    get onDidCreateInstance() { return this._onDidCreateInstance.event; }
    constructor(_instantiationService, _contextKeyService, environmentService) {
        super();
        this._instantiationService = _instantiationService;
        this._contextKeyService = _contextKeyService;
        this._backendRegistration = ( new Map());
        this._onDidCreateInstance = this._register(( new Emitter()));
        this._terminalShellTypeContextKey = TerminalContextKeys.shellType.bindTo(this._contextKeyService);
        this._terminalInRunCommandPicker = TerminalContextKeys.inTerminalRunCommandPicker.bindTo(this._contextKeyService);
        for (const remoteAuthority of [undefined, environmentService.remoteAuthority]) {
            const { promise, resolve } = promiseWithResolvers();
            this._backendRegistration.set(remoteAuthority, { promise, resolve });
        }
    }
    createInstance(config, target) {
        const shellLaunchConfig = this.convertProfileToShellLaunchConfig(config);
        const instance = this._instantiationService.createInstance(TerminalInstance, this._terminalShellTypeContextKey, this._terminalInRunCommandPicker, shellLaunchConfig);
        instance.target = target;
        this._onDidCreateInstance.fire(instance);
        return instance;
    }
    convertProfileToShellLaunchConfig(shellLaunchConfigOrProfile, cwd) {
        if (shellLaunchConfigOrProfile && 'profileName' in shellLaunchConfigOrProfile) {
            const profile = shellLaunchConfigOrProfile;
            if (!profile.path) {
                return shellLaunchConfigOrProfile;
            }
            return {
                executable: profile.path,
                args: profile.args,
                env: profile.env,
                icon: profile.icon,
                color: profile.color,
                name: profile.overrideName ? profile.profileName : undefined,
                cwd
            };
        }
        if (shellLaunchConfigOrProfile) {
            if (cwd) {
                shellLaunchConfigOrProfile.cwd = cwd;
            }
            return shellLaunchConfigOrProfile;
        }
        return {};
    }
    async getBackend(remoteAuthority) {
        let backend = ( Registry.as(TerminalExtensions.Backend)).getTerminalBackend(remoteAuthority);
        if (!backend) {
            await this._backendRegistration.get(remoteAuthority)?.promise;
            backend = ( Registry.as(TerminalExtensions.Backend)).getTerminalBackend(remoteAuthority);
        }
        return backend;
    }
    getRegisteredBackends() {
        return ( ( Registry.as(TerminalExtensions.Backend)).backends.values());
    }
    didRegisterBackend(remoteAuthority) {
        this._backendRegistration.get(remoteAuthority)?.resolve();
    }
};
TerminalInstanceService = ( __decorate([
    ( __param(0, IInstantiationService)),
    ( __param(1, IContextKeyService)),
    ( __param(2, IWorkbenchEnvironmentService))
], TerminalInstanceService));

export { TerminalInstanceService };
